import { css } from '@emotion/react';
import {
  ActionList,
  Avatar,
  Button,
  color,
  Popover,
  shadow,
  spacing
} from '@uniquegood/realworld-studio-design';
import { useMemo, useState } from 'react';

import { Height100, MarginLeftAuto, textShortening } from '@/styles';
import { MemberCardActionProps } from '../types';
import { usePlanLimits } from '@/hooks/usePlanLimits';

interface MemberCardProps extends MemberCardActionProps {
  member: User;
}

function MemberCard({
  member,
  onUpdateMember,
  onRemoveMember,
  onOpenTestContentModal
}: MemberCardProps) {
  const [isMemberToggle, setMemberToggle] = useState(false);

  const { plan } = usePlanLimits();

  // 요금제가 클래스/엔터인지 확인 (Class, Enterprise, Realworld)
  const isClassOrEnterpriseOrRealworld = useMemo(() => {
    return plan === 'Class' || plan === 'Enterprise' || plan === 'Realworld';
  }, [plan]);

  // 편집 대상 멤버의 권한에 따라 ActionListItems 필터링
  const actionListItems = useMemo(() => {
    const allItems = [
      {
        id: 'Owner',
        content: '소유자로 변경',
        onAction: () => {
          onUpdateMember(member, 'Owner');
          setMemberToggle(false);
        }
      },
      {
        id: 'Author',
        content: '편집자로 변경',
        onAction: () => {
          onUpdateMember(member, 'Author');
          setMemberToggle(false);
        }
      },
      {
        id: 'Tester',
        content: '테스터로 변경',
        onAction: () => {
          onUpdateMember(member, 'Tester');
          setMemberToggle(false);
        }
      },
      {
        id: 'testContent',
        content: '테스트 콘텐츠 변경',
        onAction: () => {
          onOpenTestContentModal(member.id);
        }
      },
      {
        id: 'destructive',
        content: '삭제',
        onAction: () => onRemoveMember(member),
        destructive: true
      }
    ];

    const memberLevel = member.level;

    // 편집 대상이 소유자(Owner)일 때
    if (memberLevel === 'Owner') {
      if (isClassOrEnterpriseOrRealworld) {
        // 클래스, 엔터: 편집자로 변경, 테스터로 변경, 삭제
        return allItems.filter(
          (item) => item.id === 'Author' || item.id === 'Tester' || item.id === 'destructive'
        );
      }
      // 무료, 프로, 티처: 테스터로 변경
      return allItems.filter((item) => item.id === 'destructive');
    }

    // 편집 대상이 편집자(Author, Developer)일 때
    if (memberLevel === 'Author' || memberLevel === 'Developer') {
      if (isClassOrEnterpriseOrRealworld) {
        // 클래스, 엔터: 소유자로 변경, 테스터로 변경, 삭제
        return allItems.filter(
          (item) => item.id === 'Owner' || item.id === 'Tester' || item.id === 'destructive'
        );
      }
      // 무료, 프로, 티처: 없음 (activator 표시 안 함)
      return [];
    }

    // 편집 대상이 테스터(Tester)일 때
    if (memberLevel === 'Tester') {
      if (isClassOrEnterpriseOrRealworld) {
        // 클래스, 엔터: 소유자로 변경, 편집자로 변경, 삭제
        return allItems.filter(
          (item) =>
            item.id === 'Owner' ||
            item.id === 'Author' ||
            item.id === 'destructive' ||
            item.id === 'testContent'
        );
      }
      // 프로, 티처: 삭제
      if (plan === 'Pro' || plan === 'Teacher') {
        return allItems.filter((item) => item.id === 'destructive' || item.id === 'testContent');
      }

      // 무료: 없음
      return [];
    }

    return [];
  }, [member.level, isClassOrEnterpriseOrRealworld, member, onUpdateMember, onRemoveMember]);

  const actionListButtonMarkup = (
    <Button
      icon="ellipsis_v_regular"
      type="plain"
      size="small"
      cssStyle={Height100}
      onClick={() => setMemberToggle(true)}
    />
  );

  // 편집자가 무료/프로/티처 요금제일 때는 activator 버튼을 표시하지 않음
  const shouldShowActivator = actionListItems.length > 0;

  return (
    <div css={MemberInfoCardStyle}>
      <Avatar userName={member.name} source={member.profileImage} cssStyle={AvatarStyle} />
      {shouldShowActivator && (
        <Popover
          active={isMemberToggle}
          activator={actionListButtonMarkup}
          onClose={() => setMemberToggle(false)}
          contentCssStyle={MarginLeftAuto}
        >
          <ActionList size="small" items={actionListItems} />
        </Popover>
      )}
    </div>
  );
}

export default MemberCard;

export const BaseMemberCardStyle = css`
  background-color: ${color.surface_default_default};
  border: 1px solid ${color.border_default_subdued};
  ${shadow.card};
  border-radius: ${spacing.common.small};
`;

const MemberInfoCardStyle = css`
  ${BaseMemberCardStyle};
  padding: ${spacing.borderedPadding.small};
  display: flex;
`;

const AvatarStyle = css`
  > *:last-of-type > .username {
    width: 96px;
    ${textShortening}
  }
`;
